import { useEffect } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import CountrySelectorInputSingle from "../form/countrySelectorInputSingle.comp";
import { LabelAndInput, LabelAndTextArea } from "../reuseables/form.comp";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { FieldValues, Path } from "react-hook-form";
import { useMemo } from "react";
import shipCore from "../../core/ship.core";
import { HkSubDistrict } from "../../types/shipSetting";
import { LabelAndSelect } from "../reuseables/form.comp";

export default function ShipInfoCreatorBasic({ methods }: { methods: UseFormReturn<any, any> }) {
  return (
    <div className="border p-4 space-y-4">
      <CountrySelectorInputSingle methods={methods} />
      <CountrySpecifiedForm methods={methods} />
    </div>
  );
}

const CountrySpecifiedForm = ({ methods }: { methods: UseFormReturn<any, any> }) => {
  const countryIso = useWatch({
    name: "countryIso",
    control: methods.control,
  });

  useEffect(() => {
    if (countryIso === "HK") {
      methods.setValue("addr.countryCode", "852");
    } else {
      methods.setValue("addr.countryCode", "");
    }
  }, [countryIso, methods]);

  switch (countryIso) {
    case "HK":
      return <FormHK methods={methods} hk={true} />;
    default:
      return <FormHK methods={methods} hk={false} />;
  }
};

const FormHK = ({ methods, hk }: { methods: UseFormReturn<any, any>; hk: boolean }) => {
  const { t, msg } = useTranslation();

  return (
    <>
      <LabelAndInput
        register={methods.register}
        fieldName="addr.recipientName"
        label={t(msg.ship.recipientName)}
        registerOptions={{
          required: true,
        }}
        type="text"
        labelCss="text-sm"
      />

      <LabelAndInput
        register={methods.register}
        fieldName="addr.telCountryCode"
        label={t(msg.ship.telCountryCode)}
        type="number"
        labelCss="text-sm"
      />

      <LabelAndInput
        register={methods.register}
        fieldName="addr.tel"
        label={t(msg.pickupAddr.tel)}
        type="tel"
        labelCss="text-sm"
      />

      {hk ? <HkDistrictSelector methods={methods} fieldName="addr.subDistrictId" /> : null}
      <LabelAndTextArea register={methods.register} fieldName="addr.address" label={t(msg.pickupAddr.address)} rows={3} />

      {!hk ? (
        <>
          <LabelAndInput register={methods.register} fieldName="addr.zipCode" label={t(msg.ship.zipcode)} />

          <LabelAndInput register={methods.register} fieldName="addr.state" label={t(msg.ship.state)} />

          <LabelAndInput register={methods.register} fieldName="addr.city" label={t(msg.ship.city)} />
        </>
      ) : null}

      <LabelAndInput
        register={methods.register}
        fieldName="addr.companyName"
        label={t(msg.ship.companyNameO)}
        // registerOptions={{
        //   required: true,
        // }}
      />
      <LabelAndTextArea
        register={methods.register}
        fieldName="addr.companyAddress"
        label={t(msg.ship.companyAddressO)}
        rows={3}
      />
    </>
  );
};

interface Props<T extends FieldValues> {
  methods: UseFormReturn<T>;
  fieldName: Path<T>;
}

interface Form {
  region: string;
  district: string;
}

function HkDistrictSelector<T extends FieldValues>({ methods, fieldName }: Props<T>) {
  const { locale, isReady } = useRouter();

  const {
    data: subDistricts,
    isFetched,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["hkSubDistricts", locale],
    queryFn: shipCore.getHkSubDistricts,
    enabled: isReady,
  });

  const internalMethods = useForm<Form>();

  useEffect(() => {
    if (isFetched && subDistricts) {
      const defaultHk = subDistricts[0];
      internalMethods.setValue("region", defaultHk.region);
      internalMethods.setValue("district", defaultHk.district);
      methods.setValue(fieldName, defaultHk.id as any);
    }
  }, [isFetched, internalMethods, subDistricts, methods, fieldName]);

  if (isError) return <h2>Error...</h2>;
  if (isFetching) return <h2>Fetching...</h2>;
  if (!isFetched) return null;
  if (!subDistricts) return null;

  return (
    <section className="flex flex-wrap gap-2 mt-4">
      <SelectRegion subDistricts={subDistricts} internalMethods={internalMethods} />
      <SelectDistrict subDistricts={subDistricts} internalMethods={internalMethods} />
      <SelectSubdistrict subDistricts={subDistricts} internalMethods={internalMethods} methods={methods} fieldName={fieldName} />
    </section>
  );
}

const SelectRegion = ({
  subDistricts,
  internalMethods,
}: {
  subDistricts: HkSubDistrict[];
  internalMethods: UseFormReturn<Form>;
}) => {
  const { t, msg } = useTranslation();
  const selectedRegion = useWatch({ name: "region", control: internalMethods.control });

  const distinctRegions = useMemo(
    () =>
      [...new Set(subDistricts.map((s) => s.region))].map((r) => {
        return { label: r, value: r };
      }),
    [subDistricts]
  );

  return (
    <LabelAndSelect
      methods={internalMethods}
      fieldName="region"
      label={t(msg.ship.region)}
      options={distinctRegions}
      defaultValue={selectedRegion}
    />
  );
};

const SelectDistrict = ({
  subDistricts,
  internalMethods,
}: {
  subDistricts: HkSubDistrict[];
  internalMethods: UseFormReturn<Form>;
}) => {
  const { t, msg } = useTranslation();

  const [selectedDistrict, selectedRegion] = useWatch({
    control: internalMethods.control,
    name: ["district", "region"],
  });

  const distinctDistricts = useMemo(
    () =>
      [...new Set(subDistricts.filter((s) => s.region === selectedRegion).map((s) => s.district))].map((r) => {
        return { label: r, value: r };
      }),
    [selectedRegion, subDistricts]
  );

  return (
    <LabelAndSelect
      methods={internalMethods}
      fieldName="district"
      label={t(msg.ship.district)}
      options={distinctDistricts}
      defaultValue={selectedDistrict}
    />
  );
};

function SelectSubdistrict<T extends FieldValues>({
  internalMethods,
  methods,
  fieldName,
  subDistricts,
}: {
  internalMethods: UseFormReturn<Form>;
  methods: UseFormReturn<T>;
  fieldName: Path<T>;
  subDistricts: HkSubDistrict[];
}) {
  const { t, msg } = useTranslation();
  const selectedSubdistrict = useWatch({ name: fieldName, control: methods.control });
  const [selectedRegion, selectedDistrict] = useWatch({
    control: internalMethods.control,
    name: ["region", "district"],
  });

  const distinctSubdistricts = useMemo(
    () =>
      [...new Set(subDistricts.filter((d) => d.region === selectedRegion && d.district === selectedDistrict))].map((s) => {
        return { label: s.subDistrict, value: s.id };
      }),
    [selectedRegion, selectedDistrict, subDistricts]
  );

  return (
    <LabelAndSelect
      methods={methods}
      fieldName={fieldName}
      label={t(msg.ship.subDistrict)}
      options={distinctSubdistricts}
      defaultValue={selectedSubdistrict}
    />
  );
}
